import { loadStripe, Stripe } from '@stripe/stripe-js';
import { config } from 'env';

/**
 * billing-manager runs against two Stripe accounts (legacy + cy). A SetupIntent
 * or PaymentIntent can only be confirmed by Stripe.js loaded with the SAME
 * account's publishable key.
 *
 * The backend tells the client WHICH account applies
 * (BillingOverview.stripe_account) but never ships the key itself; the key
 * comes from build-time config, exactly the way the single static key already
 * ships today:
 *
 *   account "legacy"        → config.stripe_pk    (the key already in the image)
 *   account "cy"            → config.stripe_pk_cy (new sibling, filled by ops)
 *   account absent / empty  → config.stripe_pk    (back-compat: a backend that
 *                             reports no account is the single-account world)
 *   anything else, or a mapped-but-unset key → FAIL LOUDLY.
 *
 * The last rule is the important one: falling back to the legacy key for a cy
 * user would mint a client_secret in one account and try to confirm it with the
 * other, which explodes deep inside Stripe.js in the middle of a checkout. An
 * explicit "checkout unavailable" is far better, so resolution throws
 * StripeAccountUnavailableError and Stripe.js is never loaded.
 *
 * Note the "account absent" rule is about a backend that ANSWERED and named no
 * account. A backend we could not reach has told us nothing, so it gets its own
 * retryable StripeAccountLookupError — not the legacy key.
 *
 * One misconfiguration has no runtime signal whatsoever, so it is caught at
 * MODULE LOAD instead: two accounts holding the SAME publishable key. That map
 * is fully mappable — every rule above passes and resolution hands back a real
 * key — so the app would cheerfully mount the legacy key under the name "cy".
 * See assertDistinctPublishableKeys.
 */

export const STRIPE_ACCOUNT_LEGACY = 'legacy';
export const STRIPE_ACCOUNT_CY = 'cy';

/** User-facing copy for the fail-loudly path (surfaced via `err.message`). */
export const STRIPE_UNAVAILABLE_MESSAGE =
  'Payment is temporarily unavailable — we could not load the payment provider. Please try again shortly or contact support.';

/**
 * User-facing copy for the retryable path: billing-manager could not be
 * reached, so which account applies is simply unknown. Distinct from the copy
 * above because this one really can come right on a retry.
 */
export const STRIPE_LOOKUP_FAILED_MESSAGE = 'We could not reach billing to set up payment. Please check your connection and try again.';

/**
 * User-facing copy for the third failure: the key resolved fine, Stripe.js
 * itself never arrived. Retryable for the same reason as the lookup failure —
 * an ad-blocker exception or a working connection makes it come right.
 */
export const STRIPE_JS_LOAD_FAILED_MESSAGE =
  'We could not load the payment provider. Please check your connection (or any ad blocker) and try again.';

/**
 * Thrown when the user's Stripe account cannot be mapped to a publishable key.
 * `message` is user-facing copy (callers already funnel `err.message` into a
 * snackbar); `account` and `reason` carry the detail for logs.
 */
export class StripeAccountUnavailableError extends Error {
  readonly account: string;

  readonly reason: string;

  constructor(account: string, reason: string) {
    super(STRIPE_UNAVAILABLE_MESSAGE);
    this.name = 'StripeAccountUnavailableError';
    this.account = account;
    this.reason = reason;
    // Keep `instanceof` working when compiled down to ES5 (CRA's target).
    Object.setPrototypeOf(this, StripeAccountUnavailableError.prototype);
  }
}

/**
 * Thrown when BillingOverview could not be reached at all, so the user's Stripe
 * account is unknown.
 *
 * This is deliberately NOT a legacy fallback. The contract's back-compat rule
 * ("no account reported → legacy") describes a backend that answered and named
 * no account; it says nothing about a backend that never answered. Guessing
 * legacy for a cy user loads Stripe.js with the wrong key while the backend
 * keeps minting client_secrets in cy, and confirmSetup then dies inside
 * Stripe.js with "No such setupintent" — the exact failure this design exists
 * to prevent. Failing here costs nothing extra: the whole billing surface
 * already depends on BillingOverview.
 *
 * `retryable` marks the difference from StripeAccountUnavailableError: a
 * transport blip really can come right on the next attempt, so this rejection
 * is never cached.
 */
export class StripeAccountLookupError extends Error {
  readonly retryable = true;

  /** The transport error underneath, for logs. */
  readonly cause: unknown;

  constructor(cause: unknown) {
    super(STRIPE_LOOKUP_FAILED_MESSAGE);
    this.name = 'StripeAccountLookupError';
    this.cause = cause;
    Object.setPrototypeOf(this, StripeAccountLookupError.prototype);
  }
}

/**
 * Thrown when Stripe.js itself could not be fetched — an ad blocker, an offline
 * tab, a CDN blip. The publishable key is right; the script never arrived.
 *
 * It exists because `loadStripe`'s rejection is the last member of the
 * truthy-sentinel class that caused the spinner bugs: a REJECTED promise is
 * still truthy, so it passes every `if (!stripe)` guard, `<Elements>` mounts on
 * it, @stripe/react-stripe-js has no error path, and the surface spins forever.
 * `stripeForKey` converts that rejection into a resolved `null`, and the null is
 * what raises this error.
 *
 * `retryable`, like StripeAccountLookupError: `stripeForKey` evicts the cache
 * entry on failure, so a retry really re-fetches the script instead of
 * replaying the same outcome.
 */
export class StripeJsLoadError extends Error {
  readonly retryable = true;

  /** The load error underneath, for logs. */
  readonly cause: unknown;

  constructor(cause?: unknown) {
    super(STRIPE_JS_LOAD_FAILED_MESSAGE);
    this.name = 'StripeJsLoadError';
    this.cause = cause;
    Object.setPrototypeOf(this, StripeJsLoadError.prototype);
  }
}

/**
 * True for the failures a "Try again" can actually resolve: billing unreachable
 * and Stripe.js unfetched. An unmappable account is a build-time fact and needs
 * a redeploy, so it is deliberately absent.
 */
export function isRetryableStripeError(err: unknown): boolean {
  return err instanceof StripeAccountLookupError || err instanceof StripeJsLoadError;
}

const byKey = new Map<string, Promise<Stripe | null>>();
const keyByUser = new Map<string, Promise<string>>();

export type BillingOverviewClientLike = {
  billingOverview: (req: { userId: string }) => Promise<unknown>;
};

/** Just the field of BillingOverview this module cares about. */
export type StripeAccountCarrier = { stripeAccount?: string };

/** Build-time config, plus the cy sibling key. */
type StripeConfig = { stripe_pk?: string; stripe_pk_cy?: string };

/** The config field each account is fed from — named in the error below. */
const CONFIG_FIELD_BY_ACCOUNT: Record<string, string> = {
  [STRIPE_ACCOUNT_LEGACY]: 'stripe_pk',
  [STRIPE_ACCOUNT_CY]: 'stripe_pk_cy'
};

/**
 * Thrown at MODULE LOAD when two accounts are configured with the same
 * publishable key. Deliberately not a checkout-time error and deliberately
 * uncaught: the app must fail to start rather than serve one checkout with the
 * wrong account's key.
 */
export class StripeDuplicateKeyError extends Error {
  /** The two account names sharing a key, for logs. */
  readonly accounts: string[];

  constructor(accounts: string[]) {
    const names = accounts.map((a) => `"${a}"`).join(' and ');
    const fields = accounts.map((a) => CONFIG_FIELD_BY_ACCOUNT[a] || a).join(' and ');
    super(
      `[stripe] Stripe accounts ${names} are configured with the SAME publishable key ` +
        `(${fields} in src/env/config.*.json). Each account needs its own key: mounting one ` +
        "account's key under another account's name loads Stripe.js with the wrong key, and that " +
        'account\'s client_secret then dies inside Stripe.js with "No such setup_intent". ' +
        'Refusing to start.'
    );
    this.name = 'StripeDuplicateKeyError';
    this.accounts = accounts;
    Object.setPrototypeOf(this, StripeDuplicateKeyError.prototype);
  }
}

/**
 * account name (as billing-manager reports it) -> build-time publishable key.
 *
 * Read out of `config` per call rather than snapshotted once, so blanking an
 * entry in a test still exercises the unset-key rule below.
 */
export function stripePublishableKeys(): Record<string, string> {
  const cfg = config as StripeConfig;
  return {
    [STRIPE_ACCOUNT_LEGACY]: cfg.stripe_pk || '',
    [STRIPE_ACCOUNT_CY]: cfg.stripe_pk_cy || ''
  };
}

/**
 * Refuse a map in which two DIFFERENT accounts carry the same NON-EMPTY key.
 *
 * EMPTY entries are not duplicates. `stripe_pk_cy` ships as "" in every env
 * config until ops fills it in, so the all-but-legacy-empty map is the normal
 * pre-flip state of every build today; an unset key is already handled, loudly
 * and per user, by resolveStripeKey.
 *
 * @throws StripeDuplicateKeyError
 */
export function assertDistinctPublishableKeys(keys: Record<string, string>): void {
  const accountByKey = new Map<string, string>();
  Object.keys(keys).forEach((account) => {
    const key = keys[account] || '';
    if (!key) return;
    const owner = accountByKey.get(key);
    if (owner !== undefined && owner !== account) throw new StripeDuplicateKeyError([owner, account]);
    accountByKey.set(key, account);
  });
}

// The replacement for the backend check this changeset retired ("cy and legacy
// publishable keys are identical"): the keys now live only in the frontends, so
// the guard does too. It has to run HERE, at module load, because a cy entry
// holding the legacy key is FULLY MAPPABLE - resolveStripeKey hands back a real
// key and every per-request check passes - so nothing downstream can catch it.
// The reply then says "cy", Stripe.js is loaded with legacy's key, and the cy
// client_secret dies with an opaque "No such setup_intent" mid-checkout: the
// exact failure this module exists to prevent.
assertDistinctPublishableKeys(stripePublishableKeys());

/** Publishable key configured for an account name, or '' when there is none. */
function keyForAccount(account: string): string {
  const keys = stripePublishableKeys();
  // hasOwnProperty rather than a bare lookup: an account named "constructor"
  // would otherwise resolve to a truthy inherited value instead of ''.
  return Object.prototype.hasOwnProperty.call(keys, account) ? keys[account] || '' : '';
}

/**
 * Publishable key for the account the backend reported.
 * @throws StripeAccountUnavailableError when the account cannot be mapped.
 */
export function resolveStripeKey(overview: StripeAccountCarrier | null | undefined): string {
  const reported = (overview?.stripeAccount || '').trim();
  // No account reported → the single-account world we have today.
  const account = reported || STRIPE_ACCOUNT_LEGACY;
  const key = keyForAccount(account);
  if (!key) {
    throw new StripeAccountUnavailableError(
      reported,
      reported
        ? `no publishable key configured for Stripe account "${reported}"`
        : 'no publishable key configured for the default (legacy) Stripe account'
    );
  }
  return key;
}

/**
 * loadStripe memoised per publishable key (two accounts → two instances).
 * @throws StripeAccountUnavailableError on an empty key — never silently falls
 *         back to the legacy key.
 *
 * NOTE the marketing site's stripeForKey (createStripeLoader in its
 * lib/stripeAccounts.js) returns null for an empty key instead of throwing:
 * there the refusal already happened, carried by an `unmapped` flag. Deliberate
 * divergence — porting a fix between the two files means moving the refusal too.
 */
export function stripeForKey(publishableKey: string): Promise<Stripe | null> {
  if (!publishableKey) {
    throw new StripeAccountUnavailableError('', 'stripeForKey called without a publishable key');
  }
  let promise = byKey.get(publishableKey);
  if (!promise) {
    promise = loadStripe(publishableKey).catch((err) => {
      // Stripe.js never arrived. Do NOT hand the rejection back: a rejected
      // promise is still truthy, so it passes every `if (!stripe)` guard,
      // <Elements> mounts on it, react-stripe-js never catches, and the surface
      // spins forever — the exact shape of the bug this module exists to kill.
      // A resolved null is a value every caller already handles.
      console.error('[stripe] Stripe.js failed to load for a publishable key', err);
      // Only evict our own entry, so a concurrent caller's fresh attempt
      // survives; the eviction is what makes `retry` re-fetch the script rather
      // than replay this failure.
      if (byKey.get(publishableKey) === promise) byKey.delete(publishableKey);
      return null;
    });
    byKey.set(publishableKey, promise);
  }
  return promise;
}

/**
 * Publishable key for a user, fetched once per session: a user's Stripe
 * account is fixed at signup and never changes in phase 1.
 *
 * Two distinct rejections, and neither one resolves a key:
 *
 *  - StripeAccountUnavailableError — the backend answered and named an account
 *    this build has no key for. CACHED: the answer cannot change without a
 *    redeploy, so re-asking would only produce the same refusal.
 *  - StripeAccountLookupError — the backend could not be reached, so the
 *    account is unknown. NOT cached, so a later attempt can still learn the
 *    user's real account. Never degrades to the legacy key: see the class doc.
 *
 * Callers must handle both. `useUserStripeState` turns either into a visible
 * "payment unavailable" surface; the `getUserStripe` call sites all sit inside
 * a catch that funnels `err.message` into an error snackbar (PurchaseModal,
 * DedicatedEnvPurchaseModal, UpgradePlanDrawer) or a `showError` (PaymentStep).
 */
export function userStripeKey(userId: string, getClient: () => Promise<BillingOverviewClientLike>): Promise<string> {
  let promise = keyByUser.get(userId);
  if (!promise) {
    promise = getClient()
      .then((client) => client.billingOverview({ userId }))
      .then((overview) => resolveStripeKey(overview as StripeAccountCarrier))
      .catch((err) => {
        if (err instanceof StripeAccountUnavailableError) throw err;
        // Only evict our own entry: a concurrent caller may already have
        // started a fresh lookup after this one failed.
        if (keyByUser.get(userId) === promise) keyByUser.delete(userId);
        throw new StripeAccountLookupError(err);
      });
    keyByUser.set(userId, promise);
  }
  return promise;
}

/**
 * Stripe.js instance for the user's account — use in async handlers.
 * Rejects with StripeAccountUnavailableError (unmappable account),
 * StripeAccountLookupError (billing unreachable) or StripeJsLoadError (the
 * script never arrived) rather than loading Stripe.js with a guessed key. Every
 * call site sits inside a catch that surfaces `err.message` to the user.
 *
 * The last one is a rejection rather than the null `stripeForKey` resolves,
 * because these call sites are async handlers shaped `stripe?.confirmX(...)`:
 * handing them null would short-circuit the whole chain into a silent no-op
 * under a spinner that never stops. The hook (`useUserStripeState`) is the one
 * that wants the null — it renders the unavailable surface from it.
 */
export async function getUserStripe(userId: string, getClient: () => Promise<BillingOverviewClientLike>): Promise<Stripe | null> {
  const stripe = await stripeForKey(await userStripeKey(userId, getClient));
  if (!stripe) throw new StripeJsLoadError();
  return stripe;
}

/** Test hook. */
export function resetUserStripeCache(): void {
  byKey.clear();
  keyByUser.clear();
}
